<template>
  <section class="full-height px-1" style="padding-block: 14px">
    <v-card v-if="isLoading" class="text-center my-4" width="270">
      <v-card-text>
        <v-progress-circular color="primary" indeterminate size="22"></v-progress-circular>
        <p class="small mt-4 font-weight-bold">
          {{ $t('yard.messages.loadingAssetVisitsData') }}...
        </p>
      </v-card-text>
    </v-card>
    <v-card elevation="0" v-else>
      <template v-slot:prepend>
        <v-icon icon="mdi-clock-outline" size="x-small"></v-icon>
      </template>
      <template v-slot:title>
        <h2 class="typography-heading-sm-bold">{{ $t('yard.messages.waitingAssignment') }}</h2>
      </template>

      <v-card-text class="mb-8">
        <div class="d-flex flex-column gap-3 pb-6 align-start">
          <span class="font-size-small font-weight-normal">
            {{ $t('yard.messages.dragAndDropCarriersToAssignThemASpot') }}.
          </span>

          <v-chip size="small" color="disabled" data-testid="yard-unassigned-list-carrier-count">
            {{ waitingAssetVisits.length }}
            {{
              waitingAssetVisits.length !== 1
                ? $t('global.specialtyTerms.carrier', 2)
                : $t('global.specialtyTerms.carrier')
            }}
          </v-chip>
        </div>

        <div v-if="waitingAssetVisits.length > 0" data-testid="yard-unassigned-list-items">
          <v-text-field
            color="primary"
            v-model="searchCarrierTerm"
            append-inner-icon="mdi-magnify"
            data-testid="yard-unassigned-list-search"
            placeholder="Filter by carrier name"
            variant="underlined"
            clearable
            @click:clear="searchCarrierTerm = ''"
            class="mb-4" />
          <div
            class="d-flex flex-column gap-4 mt-4"
            v-for="[date, dateAssetVisits] in Object.entries(groupedAssetVisitsByDate)"
            :key="date">
            <p class="font-weight-bold">{{ date }}</p>
            <yard-view-asset-visit
              v-for="assetVisit in dateAssetVisits"
              :asset-visit="assetVisit"
              :key="assetVisit.id" />
          </div>
        </div>

        <div
          v-if="searchCarrierTerm.length >= 1 && Object.keys(groupedAssetVisitsByDate).length === 0"
          data-testid="yard-unassigned-list-not-found">
          <div class="d-flex flex-column gap-4 mt-4">
            <p class="font-weight-bold">{{ $t('global.messages.noResultsFound') }}</p>
          </div>
        </div>
      </v-card-text>
      <v-card-actions
        class="card-actions"
        v-if="selectedWarehouse?.id && isYMSEnabledForSelectedWarehouse">
        <yard-view-create-assets-button :selected-warehouse="selectedWarehouse" class="w-100" />
      </v-card-actions>
    </v-card>
    <div v-if="waitingAssetVisits.length === 0" class="no-waiting-asset-visits">
      <svg-truck-asset-container
        class="mb-4"
        :rotation="90"
        background-color="white"
        border-color="grey" />
      <span class="text-center font-size-x-small">{{ $t('yard.messages.noCarriersWaiting') }}</span>
    </div>
  </section>
</template>

<script>
import { computed, defineComponent, ref } from 'vue';
import useYard from '@/modules/yard/composables/useYard';
import SvgTruckAssetContainer from '@/components/svgs/SvgTruckAssetContainer.vue';
import YardViewAssetVisit from '@/modules/yard/components/AssetVisit/Card/YardViewAssetVisit.vue';
import { LuxonDateTimeFormats } from '@nova/core';
import YardViewCreateAssetsButton from '@/modules/yard/components/AssetVisit/YardViewCreateAssetsButton.vue';

export default defineComponent({
  name: 'YardViewWaitingAssetsList',
  components: {
    YardViewAssetVisit,
    SvgTruckAssetContainer,
    YardViewCreateAssetsButton
  },
  props: {},
  setup() {
    const yardStore = useYard();

    const searchCarrierTerm = ref('');

    const searchFunction = assetVisit => {
      const lowerTerm = searchCarrierTerm.value?.toLowerCase();
      return (
        (assetVisit.code && assetVisit.code.toLowerCase().includes(lowerTerm)) ||
        (assetVisit.companyHint && assetVisit.companyHint.toLowerCase().includes(lowerTerm)) ||
        (assetVisit.phone && assetVisit.phone.toLowerCase().includes(lowerTerm)) ||
        (assetVisit.appointment?.refNumber &&
          assetVisit.appointment.refNumber.toLowerCase().includes(lowerTerm)) ||
        (assetVisit.appointment?.confirmationNumber &&
          assetVisit.appointment.confirmationNumber.toLowerCase().includes(lowerTerm)) ||
        (assetVisit.appointment?.loadType?.name &&
          assetVisit.appointment.loadType?.name.toLowerCase().includes(lowerTerm)) ||
        (assetVisit.company?.name && assetVisit.company.name.toLowerCase().includes(lowerTerm))
      );
    };

    const groupedAssetVisitsByDate = computed(() => {
      const items = {};
      const assetVisits = [...(yardStore.waitingAssetVisitsRef.value || [])];

      assetVisits
        ?.sort((a, b) => new Date(b.createDateTime) - new Date(a.createDateTime))
        ?.forEach(assetVisit => {
          const date = yardStore.formatDateTime(
            assetVisit.createDateTime,
            LuxonDateTimeFormats.DateWithAbbreviatedMonth,
            LuxonDateTimeFormats.DateWithAbbreviatedMonth
          );

          items[date] = items[date] || [];
          items[date].push(assetVisit);
        });

      // Filter results by search term
      if (searchCarrierTerm.value.length >= 1) {
        Object.keys(items).forEach(date => (items[date] = items[date].filter(searchFunction)));
      }

      // Clear out empty dates
      Object.keys(items).forEach(date => {
        if (items[date].length === 0) {
          delete items[date];
        }
      });

      // Sort items by createDateTime in descending order
      Object.keys(items).forEach(date => {
        items[date].sort((a, b) => new Date(b.createDateTime) - new Date(a.createDateTime));
      });

      return items;
    });

    return {
      searchCarrierTerm,
      selectedWarehouse: yardStore.selectedWarehouseRef,
      waitingAssetVisits: yardStore.waitingAssetVisitsRef,
      isYMSEnabledForSelectedWarehouse: yardStore.isYMSEnabledForSelectedWarehouse,
      isLoading: yardStore.isLoadingAssetVisitsRef,
      groupedAssetVisitsByDate
    };
  }
});
</script>
<style scoped lang="scss">
.no-waiting-asset-visits {
  display: flex;
  flex-direction: column;
  color: $color-text-secondary;
  align-items: center;
  justify-content: center;
  height: calc(100% - 200px);
}
</style>
